import { Box, Link, Stack, SxProps, Theme } from '@mui/material';
import { Button } from 'system/atoms';
import { RadioGroup, Checkbox } from 'common/form/components';
import { useFormik } from 'formik';
import { Channels } from 'generated-graphql/types';
import Yup from 'utils/validation';
import { CHANNEL_LABEL_BY_CHANNEL_VALUE } from 'common/constants';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';
import { CampaignCreationNextButton } from 'modules/campaign-creation/components/CampaignCreationNextButton';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { getWhatsAppConfirmForm } from 'modules/campaigns/forms/whatsAppConfirmForm';
import { campaignWhatsAppConfirmAtom } from 'modules/campaigns/stores/atom';
import { useRecoilState } from 'recoil';
import { AlertCard } from 'common/ui/AlertCard';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { CampaignCreationCSVColumns } from 'modules/campaign-creation/components/CampaignCreationCSVUpload';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { getEmailFallbackSubmitValue } from 'modules/campaign-creation/utils/getEmailFallbackSubmitValue';
import { channelsAvailable } from 'modules/campaign-creation/constants/channelsAvailable';
import { useAvailableChannels } from 'modules/campaign-creation/hooks/useAvailableChannels';
import { useFlag } from 'services/flags/hooks';
import { Flags } from 'services/flags/constants';
import { SMSAlertCard } from '../../../common/ui';

const channelBackButtonSx: SxProps<Theme> = {
  mt: 0,
  p: 0,
  fontSize: '14px',
  lineHeight: '20px'
};

export type CampaignCreationChannelOptionsProps = {
  defaultChannel: Channels | null;
  defaultEmailFallbackEnabled: boolean | null;
  isColdOutreach: boolean;
  allowWhatsAppOnColdOutreach: boolean;
  csvColumns: CampaignCreationCSVColumns | null;
  onSubmit: (values: FormValues) => void;
};

const getValidationSchema = () => {
  return Yup.object({
    channel: Yup.mixed<Channels>()
      .oneOf(channelsAvailable)
      .required('Channel is required'),
    // null = untouched, the campaign inherits the org-level setting
    emailChannelFallbackEnabled: Yup.boolean().nullable().defined()
  });
};

type FormValues = Yup.InferType<ReturnType<typeof getValidationSchema>>;

const DontShowAgainCheckbox = () => {
  const [dontShowAgain, setDontShowAgain] = useRecoilState(
    campaignWhatsAppConfirmAtom
  );

  return (
    <Checkbox
      field={{
        checkboxLabel: 'Don’t show again'
      }}
      value={dontShowAgain}
      onChange={() => setDontShowAgain(!dontShowAgain)}
    />
  );
};

export const CampaignCreationChannelOptions: React.FC<
  CampaignCreationChannelOptionsProps
> = ({
  defaultChannel,
  defaultEmailFallbackEnabled,
  isColdOutreach,
  allowWhatsAppOnColdOutreach,
  csvColumns,
  onSubmit
}) => {
  const onChannelBackToCSVUpload = useCampaignCreationStore(
    (store) => store.onChannelBackToCSVUpload
  );
  const organization = useOrganization();
  const organizationConfig = useOrganizationConfig();
  const [dontShowAgain] = useRecoilState(campaignWhatsAppConfirmAtom);

  const { openModal } = useModalV2();
  const { openDrawer } = useDrawer();

  const hideWhatsApp = isColdOutreach && !allowWhatsAppOnColdOutreach;
  const isColdOutreachWhatsAppOverride =
    isColdOutreach && allowWhatsAppOnColdOutreach;

  const { allChannels } = useAvailableChannels({
    csvColumns: csvColumns || null,
    hideWhatsApp
  });

  // Flag defaults on; only an explicit `false` from PostHog hides the checkbox
  const isEmailFallbackHidden =
    useFlag(Flags.CAMPAIGN_EMAIL_FALLBACK_TOGGLE) === false;
  const isEmailFallbackDisabled =
    isEmailFallbackHidden || (!!csvColumns && !csvColumns.email);

  const toSubmitValues = (values: FormValues): FormValues => ({
    ...values,
    emailChannelFallbackEnabled: getEmailFallbackSubmitValue({
      channel: values.channel,
      emailChannelFallbackEnabled: values.emailChannelFallbackEnabled,
      isEmailFallbackDisabled
    })
  });

  const formik = useFormik({
    initialValues: {
      channel: defaultChannel,
      emailChannelFallbackEnabled: defaultEmailFallbackEnabled
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      if (values.channel === Channels.Whatsapp && !dontShowAgain) {
        openModal({
          width: '380px',
          title: 'Continue with WhatsApp?',
          subtitle: `High rejection rates from recipients will permanently and irrevocably block the ${organization.name} WhatsApp number.`,
          form: getWhatsAppConfirmForm(isColdOutreachWhatsAppOverride),
          onPrimaryAction: () => {
            // NOTE: We cast it here as formik doesn't work correctly with the types
            // It uses the initial values as the type which is incorrect
            onSubmit(toSubmitValues(formik.values as FormValues));
          },
          afterActionsChildren: <DontShowAgainCheckbox />
        });
        return;
      }
      // NOTE: We cast it here as formik doesn't work correctly with the types
      // It uses the initial values as the type which is incorrect
      onSubmit(toSubmitValues(values as FormValues));
    }
  });

  const hasDisabledPhoneChannels = allChannels.some(
    (channel) =>
      (channel.value === Channels.Whatsapp || channel.value === Channels.Sms) &&
      channel.disabled
  );
  const hasDisabledEmailChannel = allChannels.some(
    (channel) => channel.value === Channels.Email && channel.disabled
  );

  const isPhoneChannelSelected =
    formik.values.channel === Channels.Whatsapp ||
    formik.values.channel === Channels.Sms;
  // null = untouched: the checkbox shows the inherited org default
  const effectiveEmailFallback =
    formik.values.emailChannelFallbackEnabled ??
    !!organizationConfig?.emailChannelFallbackEnabled;

  return (
    <Stack spacing={5}>
      {(hasDisabledPhoneChannels || hasDisabledEmailChannel) && (
        <AlertCard
          title={
            hasDisabledPhoneChannels
              ? 'SMS and WhatsApp unavailable'
              : 'Email unavailable'
          }
          type="info"
          sx={{
            maxWidth: '700px'
          }}
        >
          <Stack alignItems="flex-start" direction="row">
            {hasDisabledPhoneChannels
              ? 'No phone numbers were found in the CSV uploaded.'
              : 'No email addresses were found in the CSV uploaded.'}
            <Button
              variant="tertiary"
              onClick={onChannelBackToCSVUpload}
              sx={channelBackButtonSx}
            >
              Go back
            </Button>
          </Stack>
        </AlertCard>
      )}
      <RadioGroup
        field={{
          label: 'Choose channel',
          options: allChannels
        }}
        value={formik.values.channel}
        errorMessage={
          formik.touched.channel ? formik.errors.channel : undefined
        }
        onChange={(value) => formik.setFieldValue('channel', value as Channels)}
        onBlur={formik.handleBlur}
      />
      {isPhoneChannelSelected && !isEmailFallbackHidden && (
        <Checkbox
          field={{
            checkboxLabel: `Send via email if ${
              CHANNEL_LABEL_BY_CHANNEL_VALUE[formik.values.channel as Channels]
            } fails`,
            subLabel: isEmailFallbackDisabled
              ? 'No email column was mapped in the CSV uploaded.'
              : undefined,
            disabled: isEmailFallbackDisabled
          }}
          value={effectiveEmailFallback}
          onChange={(checked) =>
            formik.setFieldValue('emailChannelFallbackEnabled', checked)
          }
        />
      )}
      {formik.values.channel === Channels.Whatsapp && (
        <AlertCard
          title="Use WhatsApp when recipients expect a message"
          type="info"
          sx={{
            maxWidth: '700px'
          }}
        >
          <Box>
            Your number will be blocked if the campaign is irrelevant to them.{' '}
            <Link
              component="button"
              sx={{ mb: '3px' }}
              onClick={() =>
                openDrawer({
                  drawerContentType:
                    DrawerComponentType.WhatsAppLearnMoreDrawer,
                  drawerComponentProps: {
                    organizationName: organization.name
                  }
                })
              }
            >
              Learn more
            </Link>
          </Box>
        </AlertCard>
      )}
      {formik.values.channel === Channels.Sms && (
        <SMSAlertCard twilioPhoneNumber={organization?.twilioPhoneNumber} />
      )}
      <Stack direction="row" gap={2}>
        <CampaignCreationNextButton handleNext={formik.handleSubmit} />
      </Stack>
    </Stack>
  );
};
